import moment from 'moment'

const tomorrow = moment()
  .add(1, 'days')
  .format('YYYYMMDD')

export const INITIAL_RESERVE_CONFIG = {
  mode: 'immediate',
  isEdit: false,
  scheduledDate: tomorrow,
  title: undefined,
  cycleType: 'ONCE',
  errors: [],
  reservationGroupId: undefined,
}

/**
 * 予約ステータス定義と権限管理
 *
 * 各ステータスの権限（削除可能、編集可能、再実行可能）を集約管理
 */
export const RESERVATION_STATUSES = {
  WAITING: {
    value: 'Waiting',
    label: '待機中',
    icon: 'timer',
    tooltip: '実行待ちです',
    canDelete: true,
    canEdit: true,
    canResubmit: false,
    description: 'キュー入待機中',
  },
  QUEUED: {
    value: 'Queued',
    label: 'キュー',
    icon: 'build',
    tooltip: '分析中です',
    canDelete: false,
    canEdit: false,
    canResubmit: false,
    description: '処理キューに入った状態',
  },
  RUNNING: {
    value: 'Running',
    label: '実行中',
    icon: 'build',
    tooltip: '分析中です',
    canDelete: false,
    canEdit: false,
    canResubmit: false,
    description: '分析を実行中',
  },
  ERROR: {
    value: 'Error',
    label: 'エラー',
    icon: 'error',
    tooltip: 'エラーが発生しました',
    canDelete: false,
    canEdit: false,
    canResubmit: true,
    description: 'エラーで終了',
  },
  COMPLETED: {
    value: 'Completed',
    label: '完了',
    icon: 'check',
    tooltip: '分析完了しています',
    canDelete: false,
    canEdit: false,
    canResubmit: true,
    description: '分析完了',
  },
  PAUSED: {
    value: 'Paused',
    label: '一時停止',
    icon: 'pause',
    tooltip: '一時停止中です',
    canDelete: true,
    canEdit: true,
    canResubmit: false,
    description: '一時停止中',
  },
  REQUIRES_UPDATE: {
    value: 'RequiresUpdate',
    label: '更新が必要',
    icon: 'warning',
    tooltip: '分析条件の構成が変更されました。予約を編集してください。',
    canDelete: true,
    canEdit: true,
    canResubmit: false,
    description: '構成の変更が必要な状態',
  },
  NEXT_RESERVATION_FAILED: {
    value: 'NextReservationFailed',
    label: '次回予約失敗',
    icon: 'check',
    tooltip: '次回予約作成に失敗しました。次回予約を作成してください。',
    canDelete: false,
    canEdit: false,
    canResubmit: true,
    description: '定期実行の次回予約作成に失敗',
  },
}

/**
 * 分析履歴ステータス定義
 *
 * 分析履歴（クエリ実行）のステータスとアイコン情報を集約管理
 */
export const QUERY_STATUSES = {
  WORKING: {
    value: 'Working',
    label: '処理中',
    icon: 'build',
    tooltip: '分析中です',
  },
  COMPLETED: {
    value: 'Completed',
    label: '完了',
    icon: 'check',
    tooltip: '分析完了しています',
  },
  ERROR: {
    value: 'Error',
    label: 'エラー',
    icon: 'error',
    tooltip: 'エラーが発生しました',
  },
}

/**
 * 予約ステータス：ステータス値から対応するステータスオブジェクトを取得
 * @param {string} statusValue - ステータス値（'RequiresUpdate', 'Waiting' など）
 * @returns {Object|undefined} ステータスオブジェクト
 */
export const getReservationStatus = statusValue => {
  return Object.values(RESERVATION_STATUSES).find(s => s.value === statusValue)
}

/**
 * 分析履歴ステータス：ステータス値から対応するステータスオブジェクトを取得
 * @param {string} statusValue - ステータス値（'Working', 'Completed', 'Error' など）
 * @returns {Object|undefined} ステータスオブジェクト
 */
export const getQueryStatus = statusValue => {
  return Object.values(QUERY_STATUSES).find(s => s.value === statusValue)
}

/**
 * 予約ステータスが削除可能かどうかを判定
 * @param {string} statusValue - ステータス値
 * @returns {boolean} 削除可能な場合 true
 */
export const canDeleteReservation = statusValue => {
  const status = getReservationStatus(statusValue)
  if (!status) return false
  return status.canDelete
}

/**
 * 予約ステータスが編集可能かどうかを判定
 * @param {string} statusValue - ステータス値
 * @returns {boolean} 編集可能な場合 true
 */
export const canEditReservation = statusValue => {
  const status = getReservationStatus(statusValue)
  if (!status) return false
  return status.canEdit
}

/**
 * 予約ステータスが再実行可能かどうかを判定
 * @param {string} statusValue - ステータス値
 * @returns {boolean} 再実行可能な場合 true
 */
export const canResubmitReservation = statusValue => {
  const status = getReservationStatus(statusValue)
  if (!status) return false
  return status.canResubmit
}

/**
 * 予約ステータスラベルを取得
 * @param {string} statusValue - ステータス値
 * @returns {string} 日本語ラベル
 */
export const getReservationStatusLabel = statusValue => {
  const status = getReservationStatus(statusValue)
  if (!status) return '不明'
  return status.label
}

/**
 * 全ステータス値の配列（RESERVATION_STATUSESから動的に生成）
 */
export const STATUSES = Object.values(RESERVATION_STATUSES).map(s => s.value)
